/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   useMaintenanceData.ts
 *     Description     :   React Query hooks for maintenance data
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-01-19 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.11.0 (Specification Testing Standards Edition)
 *     Feature         :   028-lakehouse-maintenance
 * =========================================================================================
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions, keepPreviousData } from '@tanstack/react-query';
import * as maintenanceApi from '../services/maintenanceApi';
import type {
  DashboardSummary,
  LakehouseWithHealth,
  LakehouseFilters,
  MaintenanceHistoryFilters,
} from '../services/maintenanceApi';
import type { MaintenanceHistory, MaintenancePolicy } from '../../../shared/types/maintenance';

// ============================================================================
// Query Keys
// ============================================================================

export const maintenanceKeys = {
  all: ['maintenance'] as const,
  dashboardSummary: () => [...maintenanceKeys.all, 'dashboard-summary'] as const,
  lakehouses: (page?: number, pageSize?: number, filters?: LakehouseFilters) =>
    [...maintenanceKeys.all, 'lakehouses', { page, pageSize, filters }] as const,
  history: (page?: number, pageSize?: number, filters?: MaintenanceHistoryFilters) =>
    [...maintenanceKeys.all, 'history', { page, pageSize, filters }] as const,
  historyRecord: (id: string) => [...maintenanceKeys.all, 'history', id] as const,
  lakehouseHistory: (lakehouseId: string, page?: number) =>
    [...maintenanceKeys.all, 'lakehouse-history', lakehouseId, page] as const,
  policies: (page?: number) => [...maintenanceKeys.all, 'policies', page] as const,
  policy: (id: string) => [...maintenanceKeys.all, 'policies', id] as const,
  resolvedPolicy: (lakehouseId: string) =>
    [...maintenanceKeys.all, 'resolved-policy', lakehouseId] as const,
};

// ============================================================================
// Dashboard Hooks
// ============================================================================

/**
 * Hook to fetch dashboard summary
 */
export function useDashboardSummary(options?: UseQueryOptions<DashboardSummary>) {
  return useQuery<DashboardSummary>({
    queryKey: maintenanceKeys.dashboardSummary(),
    queryFn: maintenanceApi.getDashboardSummary,
    staleTime: 30_000, // 30 seconds
    refetchInterval: 60_000, // Refetch every minute
    ...options,
  });
}

/**
 * Hook to fetch lakehouses with health data
 */
export function useLakehousesWithHealth(
  page: number = 1,
  pageSize: number = 20,
  filters?: LakehouseFilters,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc'
) {
  return useQuery({
    queryKey: maintenanceKeys.lakehouses(page, pageSize, filters),
    queryFn: () => maintenanceApi.getLakehousesWithHealth(page, pageSize, filters, sortBy, sortOrder),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ============================================================================
// History Hooks
// ============================================================================

/**
 * Hook to fetch maintenance history
 */
export function useMaintenanceHistory(
  page: number = 1,
  pageSize: number = 20,
  filters?: MaintenanceHistoryFilters
) {
  return useQuery({
    queryKey: maintenanceKeys.history(page, pageSize, filters),
    queryFn: () => maintenanceApi.getMaintenanceHistory(page, pageSize, filters),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to fetch history for a specific lakehouse
 */
export function useLakehouseHistory(lakehouseId: string, page: number = 1, pageSize: number = 10) {
  return useQuery({
    queryKey: maintenanceKeys.lakehouseHistory(lakehouseId, page),
    queryFn: () => maintenanceApi.getLakehouseHistory(lakehouseId, page, pageSize),
    staleTime: 15_000,
    enabled: !!lakehouseId,
  });
}

/**
 * Hook to fetch single history record
 */
export function useHistoryRecord(historyId: string) {
  return useQuery({
    queryKey: maintenanceKeys.historyRecord(historyId),
    queryFn: () => maintenanceApi.getHistoryRecord(historyId),
    staleTime: 30_000,
    enabled: !!historyId,
  });
}

// ============================================================================
// Policy Hooks
// ============================================================================

/**
 * Hook to fetch all policies
 */
export function usePolicies(page: number = 1, pageSize: number = 50) {
  return useQuery({
    queryKey: maintenanceKeys.policies(page),
    queryFn: () => maintenanceApi.getPolicies(page, pageSize),
    staleTime: 60_000,
  });
}

/**
 * Hook to fetch single policy
 */
export function usePolicy(policyId: string) {
  return useQuery({
    queryKey: maintenanceKeys.policy(policyId),
    queryFn: () => maintenanceApi.getPolicy(policyId),
    staleTime: 60_000,
    enabled: !!policyId,
  });
}

/**
 * Hook to fetch resolved policy for lakehouse
 */
export function useResolvedPolicy(lakehouseId: string) {
  return useQuery({
    queryKey: maintenanceKeys.resolvedPolicy(lakehouseId),
    queryFn: () => maintenanceApi.resolvePolicy(lakehouseId),
    staleTime: 30_000,
    enabled: !!lakehouseId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to create policy
 */
export function useCreatePolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (policy: Partial<MaintenancePolicy>) => maintenanceApi.createPolicy(policy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maintenanceKeys.policies() });
    },
  });
}

/**
 * Hook to update policy
 */
export function useUpdatePolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, policy }: { id: string; policy: Partial<MaintenancePolicy> }) =>
      maintenanceApi.updatePolicy(id, policy),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: maintenanceKeys.policies() });
      queryClient.invalidateQueries({ queryKey: maintenanceKeys.policy(data.id) });
    },
  });
}

/**
 * Hook to delete policy
 */
export function useDeletePolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (policyId: string) => maintenanceApi.deletePolicy(policyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maintenanceKeys.policies() });
    },
  });
}
